import React, { createContext, useContext, useMemo, useState, useCallback, useEffect } from 'react';
import type {
  RegistrationData,
  UserData,
  ECKMConditionsData,
  CKMConditionsData,
  BehavioralHealthConditionsData,
  BehavioralHealthScreenerData,
  EligibilityResult,
  EligibilityTrackResult,
} from '@/types/registration';
import {
  getNextStepAfterECKM,
  getNextStepAfterCKM,
  getNextStepAfterBH,
} from '@/utils/eligibility';
import { encryptData, decryptData } from '@/utils/crypto';
import { isCrisisResponse, PHQ9_Q9_INDEX } from '@/utils/bhScreener';
import { generateAccessWebregUserId, WEBREG_USER_ID_STORAGE_KEY } from '@/utils/webregUserIdGenerator';
import { saveUTMParams, UTMParams } from '@/utils/utm';
import { generateSampleGroup } from '@/utils/abTest';
import analytics from '@/services/Analytics';

type RegistrationContextValue = {
  registrationData: RegistrationData;
  isLoaded: boolean;
  updateMedicareCoverage: (data: Partial<RegistrationData['medicareCoverage']>) => void;
  updateECKMConditions: (data: ECKMConditionsData) => void;
  updateCKMConditions: (data: CKMConditionsData) => void;
  updateBehavioralHealthConditions: (data: BehavioralHealthConditionsData) => void;
  updateBHScreener: (data: Partial<BehavioralHealthScreenerData>) => void;
  updateEligibility: (eligibility: EligibilityResult) => void;
  upsertEligibilityResult: (trackResult: EligibilityTrackResult) => void;
  updateSelectedTracks: (tracks: RegistrationData['selectedTracks']) => void;
  setPromoCodeData: (promoCodeData: RegistrationData['promoCodeData']) => void;
  updateAccount: (data: Partial<RegistrationData['account']>) => void;
  updateShipping: (data: Partial<RegistrationData['shipping']>) => void;
  setUserData: (userData: UserData) => void;
  hasFinishedShipping: boolean;
  setHasFinishedShipping: (finished: boolean) => void;
  getNextRouteAfterECKM: () => string;
  getNextRouteAfterCKM: () => string;
  getNextRouteAfterBH: () => string;
  clearRegistrationData: () => void;
  webregUserId: string;
  sampleGroup: string;
  utmParams: UTMParams | null;
};

const RegistrationContext = createContext<RegistrationContextValue | undefined>(undefined);

// Storage key for encrypted data
const STORAGE_KEY = 'hh_access_reg_data';

const initialData: RegistrationData = {
  medicareCoverage: {
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    zipCode: '',
    mbi: '',
    gender: null,
  },
  eCKMConditions: {
    selectedConditions: new Set(),
    completed: false,
  },
  ckmConditions: {
    selectedConditions: new Set(),
    completed: false,
  },
  behavioralHealthConditions: {
    selectedConditions: new Set(),
    completed: false,
  },
  bhScreener: {
    phq9Answers: [],
    phq9Score: null,
    gad7Answers: [],
    gad7Score: null,
    crisisGateShown: false,
    completed: false,
  },
  eligibility: null,
  eligibilityResults: [],
  selectedTracks: [],
  account: {
    email: '',
    password: '',
    retypePassword: '',
    phoneNumber: '',
  },
  shipping: {
    firstName: '',
    lastName: '',
    streetAddress: '',
    apartment: '',
    city: '',
    state: '',
    zipCode: '',
  },
  userData: null,
  promoCodeData: null,
};

/**
 * Helper to pick specific fields from an object (whitelist approach)
 */
function pickFields<T, K extends keyof T>(obj: T, keys: readonly K[]): Pick<T, K> {
  const result = {} as Pick<T, K>;
  keys.forEach(key => {
    result[key] = obj[key];
  });
  return result;
}

/**
 * Type for encrypted storage
 * Excludes: password, retypePassword, healthConditions (deprecated field - never stored)
 */
type StoredData = {
  medicareCoverage: Pick<RegistrationData['medicareCoverage'], 'firstName' | 'lastName' | 'dateOfBirth' | 'zipCode' | 'mbi' | 'gender'>;
  eCKMConditions: RegistrationData['eCKMConditions'];
  ckmConditions: RegistrationData['ckmConditions'];
  behavioralHealthConditions: RegistrationData['behavioralHealthConditions'];
  // crisisGateShown excluded — it's a near-direct proxy for a Q9 suicidality
  // response and is fully recomputable from phq9Answers, so there's no reason
  // to also persist it as an explicit boolean flag.
  bhScreener: Omit<RegistrationData['bhScreener'], 'crisisGateShown'>;
  eligibility: RegistrationData['eligibility'];
  eligibilityResults: RegistrationData['eligibilityResults'];
  selectedTracks: RegistrationData['selectedTracks'];
  account: Pick<RegistrationData['account'], 'email' | 'phoneNumber'>;
  shipping: RegistrationData['shipping'];
};

/**
 * Load persisted data from sessionStorage
 * - All data encrypted with AES-GCM
 * - Never stored: Passwords, MBI
 * - Stored: `bhScreener`, incl. raw PHQ-9/GAD-7 answers — pending PRD 11 §7
 *   pre-launch risk-acceptance on whether raw screener answers should persist
 *   client-side at all (Daniel/Edo). `crisisGateShown` itself is NOT persisted
 *   (recomputed from `phq9Answers` on load) to avoid also storing an explicit
 *   Q9-suicidality-proxy flag.
 */
const loadPersistedData = async (): Promise<RegistrationData> => {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (!stored) {
      return initialData;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const decryptedData = await decryptData<any>(stored);

    // Helper to convert array back to Set (needed after JSON deserialization)
    // Sets are serialized as arrays in JSON, so we need to convert them back
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const toSet = <T,>(value: any): Set<T> => {
      if (value instanceof Set) return value;
      if (Array.isArray(value)) return new Set(value);
      if (value?.selectedConditions) {
        if (value.selectedConditions instanceof Set) return value.selectedConditions;
        if (Array.isArray(value.selectedConditions)) return new Set(value.selectedConditions);
      }
      return new Set();
    };

    // Merge with initialData, excluding passwords only
    return {
      ...initialData,
      medicareCoverage: {
        ...initialData.medicareCoverage,
        ...decryptedData.medicareCoverage,
      },
      eCKMConditions: {
        selectedConditions: toSet(decryptedData.eCKMConditions),
        completed: decryptedData.eCKMConditions?.completed ?? false,
      },
      ckmConditions: {
        selectedConditions: toSet(decryptedData.ckmConditions),
        completed: decryptedData.ckmConditions?.completed ?? false,
      },
      behavioralHealthConditions: {
        selectedConditions: toSet(decryptedData.behavioralHealthConditions),
        completed: decryptedData.behavioralHealthConditions?.completed ?? false,
      },
      // RND-35692: screener answers/scores are plain arrays/numbers — a shallow
      // merge over the defaults rehydrates them and tolerates pre-screener sessions.
      // crisisGateShown is never stored — recompute it from phq9Answers so a
      // rehydrated session doesn't lose the Q9 crisis-gate precedence signal.
      bhScreener: {
        ...initialData.bhScreener,
        ...decryptedData.bhScreener,
        crisisGateShown: isCrisisResponse(
          decryptedData.bhScreener?.phq9Answers?.[PHQ9_Q9_INDEX] ?? 0,
        ),
      },
      eligibility: decryptedData.eligibility ?? initialData.eligibility,
      eligibilityResults: decryptedData.eligibilityResults ?? initialData.eligibilityResults,
      selectedTracks: decryptedData.selectedTracks ?? initialData.selectedTracks,
      account: {
        ...initialData.account,
        ...decryptedData.account,
        // Force clear password fields (never persisted)
        password: '',
        retypePassword: '',
      },
      shipping: {
        ...initialData.shipping,
        ...decryptedData.shipping,
      },
    };
  } catch (error) {
    console.warn('Failed to load persisted registration data:', error);
    return initialData;
  }
};

const RegistrationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [registrationData, setRegistrationData] = useState<RegistrationData>(initialData);
  const [hasFinishedShipping, setHasFinishedShipping] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  // Initialize webregUserId - generate or retrieve from localStorage
  const [webregUserId] = useState<string>(() => {
    return localStorage?.getItem?.(WEBREG_USER_ID_STORAGE_KEY) || generateAccessWebregUserId();
  });

  // Initialize sampleGroup - generate or retrieve from localStorage
  const [sampleGroup] = useState<string>(() => {
    return generateSampleGroup();
  });

  // Initialize UTM params - save from URL or retrieve from localStorage
  const [utmParams] = useState<UTMParams | null>(() => {
    const previousUTMParams = localStorage.getItem('utmParams');
    return saveUTMParams() || (previousUTMParams && JSON.parse(previousUTMParams));
  });

  // Register webregUserId with analytics on mount
  useEffect(() => {
    if (webregUserId) {
      analytics.registerUser(webregUserId);
    }
  }, [webregUserId]);

  // Load persisted data on mount
  useEffect(() => {
    loadPersistedData()
      .then(data => {
        setRegistrationData(data);
        setIsLoaded(true);
      })
      .catch(error => {
        console.warn('Failed to load persisted data:', error);
        setIsLoaded(true);
      });
  }, []);

  // Persist data to sessionStorage whenever it changes (after initial load)
  useEffect(() => {
    if (!isLoaded) return; // Skip saving until initial load is complete

    const saveData = async () => {
      try {
        // Whitelist: Only store fields we explicitly allow (excluding passwords and deprecated healthConditions)
        const dataToStore: StoredData = {
          medicareCoverage: pickFields(registrationData.medicareCoverage, ['firstName', 'lastName', 'dateOfBirth', 'zipCode', 'mbi', 'gender'] as const),
          eCKMConditions: registrationData.eCKMConditions,
          ckmConditions: registrationData.ckmConditions,
          behavioralHealthConditions: registrationData.behavioralHealthConditions,
          bhScreener: pickFields(registrationData.bhScreener, [
            'phq9Answers', 'phq9Score', 'gad7Answers', 'gad7Score', 'completed',
          ] as const),
          eligibility: registrationData.eligibility,
          eligibilityResults: registrationData.eligibilityResults,
          selectedTracks: registrationData.selectedTracks,
          account: pickFields(registrationData.account, ['email', 'phoneNumber'] as const),
          shipping: registrationData.shipping,
        };

        // Encrypt and store whitelisted data
        const encrypted = await encryptData(dataToStore);
        sessionStorage.setItem(STORAGE_KEY, encrypted);
      } catch (error) {
        console.warn('Failed to persist registration data:', error);
      }
    };

    saveData();
  }, [registrationData, isLoaded]);

  const updateMedicareCoverage = useCallback((data: Partial<RegistrationData['medicareCoverage']>) => {
    setRegistrationData(prev => ({
      ...prev,
      medicareCoverage: { ...prev.medicareCoverage, ...data },
    }));
  }, []);

  const updateECKMConditions = (data: ECKMConditionsData) => {
    setRegistrationData(prev => ({
      ...prev,
      eCKMConditions: data, // Overwrite entirely
    }));
  };

  const updateCKMConditions = (data: CKMConditionsData) => {
    setRegistrationData(prev => ({
      ...prev,
      ckmConditions: data, // Overwrite entirely
    }));
  };

  const updateBehavioralHealthConditions = (data: BehavioralHealthConditionsData) => {
    setRegistrationData(prev => ({
      ...prev,
      behavioralHealthConditions: data, // Overwrite entirely
    }));
  };

  // RND-35692: merge partial screener updates (answers/scores/flags accumulate
  // across the PHQ-9 then GAD-7 steps). Client-side only — never submitted.
  const updateBHScreener = useCallback((data: Partial<BehavioralHealthScreenerData>) => {
    setRegistrationData(prev => ({
      ...prev,
      bhScreener: { ...prev.bhScreener, ...data },
    }));
  }, []);

  const updateEligibility = useCallback((eligibility: EligibilityResult) => {
    setRegistrationData(prev => ({
      ...prev,
      eligibility,
    }));
  }, []);

  const upsertEligibilityResult = useCallback((trackResult: EligibilityTrackResult) => {
    setRegistrationData(prev => ({
      ...prev,
      eligibilityResults: [
        ...prev.eligibilityResults.filter(r => r.track !== trackResult.track),
        trackResult,
      ],
    }));
  }, []);

  const updateSelectedTracks = useCallback((tracks: RegistrationData['selectedTracks']) => {
    setRegistrationData(prev => ({
      ...prev,
      selectedTracks: tracks,
    }));
  }, []);

  const setPromoCodeData = useCallback((promoCodeData: RegistrationData['promoCodeData']) => {
    setRegistrationData(prev => ({
      ...prev,
      promoCodeData,
    }));
  }, []);

  const updateAccount = useCallback((data: Partial<RegistrationData['account']>) => {
    setRegistrationData(prev => ({
      ...prev,
      account: { ...prev.account, ...data },
    }));
  }, []);

  const clearRegistrationData = useCallback(() => {
    setRegistrationData(initialData);
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      console.warn('Failed to clear persisted registration data:', error);
    }
  }, []);

  const updateShipping = useCallback((data: Partial<RegistrationData['shipping']>) => {
    setRegistrationData(prev => ({
      ...prev,
      shipping: { ...prev.shipping, ...data },
    }));
  }, []);

  const setUserData = useCallback((userData: UserData) => {
    setRegistrationData(prev => ({
      ...prev,
      userData,
    }));
  }, []);

  // Navigation helpers
  const getNextRouteAfterECKM = useCallback(() => {
    return getNextStepAfterECKM();
  }, []);

  const getNextRouteAfterCKM = useCallback(() => {
    return getNextStepAfterCKM();
  }, []);

  const getNextRouteAfterBH = useCallback(() => {
    if (!registrationData.eligibility) {
      return '/not-eligible';
    }
    return getNextStepAfterBH(registrationData.eligibility);
  }, [registrationData.eligibility]);

  const value = useMemo(
    () => ({
      registrationData,
      isLoaded,
      updateMedicareCoverage,
      updateECKMConditions,
      updateCKMConditions,
      updateBehavioralHealthConditions,
      updateBHScreener,
      updateEligibility,
      upsertEligibilityResult,
      updateSelectedTracks,
      setPromoCodeData,
      updateAccount,
      updateShipping,
      setUserData,
      hasFinishedShipping,
      setHasFinishedShipping,
      getNextRouteAfterECKM,
      getNextRouteAfterCKM,
      getNextRouteAfterBH,
      clearRegistrationData,
      webregUserId,
      sampleGroup,
      utmParams,
    }),
    [
      registrationData,
      isLoaded,
      updateMedicareCoverage,
      setPromoCodeData,
      updateAccount,
      updateShipping,
      updateBHScreener,
      updateEligibility,
      upsertEligibilityResult,
      updateSelectedTracks,
      setUserData,
      hasFinishedShipping,
      getNextRouteAfterECKM,
      getNextRouteAfterCKM,
      getNextRouteAfterBH,
      clearRegistrationData,
      webregUserId,
      sampleGroup,
      utmParams,
    ],
  );

  return <RegistrationContext.Provider value={value}>{children}</RegistrationContext.Provider>;
};

const useRegistration = () => {
  const context = useContext(RegistrationContext);
  if (!context) {
    throw new Error('useRegistration must be used within a RegistrationProvider');
  }
  return context;
};

export { RegistrationProvider, useRegistration };
